import {
  all,
  equals,
  find,
  forEach,
  isEmpty,
  isNil,
  map,
  omit,
  pick,
  pipe,
  prop,
  propEq,
} from "ramda";

import { saveOrderInfoToLocalStorage } from "helpers/local_storage";
import { validateBlurredField } from "helpers/validation/forms";
import VALIDATION_MESSAGES from "helpers/validation/messages";
import Validators, {
  contactInfoValidators,
  phoneRegex,
} from "helpers/validation/validators";
import { postPhoneNumber, putConsumerInfo } from "interfaces/consumer_info";
import { postOrder } from "interfaces/order";
import getOrderingInfo, {
  getItemStockLevelInfo,
} from "interfaces/ordering_info";
import isLoggedIn from "is_logged_in";
import { emptyCart } from "packages/online_ordering/actions/cart";
import {
  addOrderConfirmationDetails,
  parseOrderSubmissionError,
  returnToMenu,
  updateContactInfo,
  updateOrderStatus,
} from "packages/online_ordering/actions/checkout";
import { setOrderTimeError } from "packages/online_ordering/actions/ordering_options";
import { orderTransform } from "packages/online_ordering/api/helpers/order_transform";
import type { PaymentConfiguration } from "packages/online_ordering/components/checkout/payment/PaymentConfiguration";
import { orderTimeExpired } from "packages/online_ordering/helpers/orderTimeExpired";
import type { CartState } from "packages/online_ordering/reducers/cart";
import type { CheckoutState } from "packages/online_ordering/reducers/checkout";
import type { ConsumerState } from "packages/online_ordering/reducers/consumer/consumer";
import { updateOrderingInfo } from "packages/online_ordering/reducers/orderingInfo";
import type { IOrderPayload } from "packages/online_ordering/types/IOrderPayload";
import type { IOrderingOptionsState } from "types/reducers/ordering_options";
import type { IErrors, INewCreditCard } from "types/reducers/payment";
import type { StoreInfo } from "types/reducers/store_info";

export const RETURN_CHECKOUT_FORM_ERRORS =
  "online_ordering/RETURN_CHECKOUT_FORM_ERRORS";
export const CLEAR_CHECKOUT_FORM_ERRORS =
  "online_ordering/CLEAR_CHECKOUT_FORM_ERRORS";
export const VALIDATE_CC_FIELD_ON_BLUR =
  "online_ordering/VALIDATE_CC_FIELD_ON_BLUR";
export const VALIDATE_CONTACT_FIELD_ON_BLUR =
  "online_ordering/VALIDATE_CONTACT_FIELD_ON_BLUR";

function validateContactInfo(contactInfo, validators) {
  const errors = [];

  if (!Validators.isValid(contactInfo, validators, errors)) {
    return pick(errors, VALIDATION_MESSAGES);
  }
  // HACK: this section needs rewriting it's duplicating a lot of different checks
  // but we have to solve an issue for a customer immediately
  let sanitizedErrors = contactInfo.errors;
  if (!isEmpty(contactInfo.errors)) {
    // We know that the following fields are valid even if they haven't been omitted
    // from state yet
    sanitizedErrors = omit(["firstName", "lastName", "email"], sanitizedErrors);
  }

  if (!isEmpty(sanitizedErrors)) {
    // These could contain errors from phone NANP validation

    return sanitizedErrors;
  }

  return null;
}

function clearCheckoutFormErrors() {
  return {
    type: CLEAR_CHECKOUT_FORM_ERRORS,
  };
}

function fetchMenu() {
  return dispatch => {
    Promise.all([getOrderingInfo(), getItemStockLevelInfo()])
      .then(([orderingInfo, stockLevels]) => {
        dispatch(updateOrderingInfo({ ...orderingInfo, stockLevels }));
      })
      .catch(e => console.error(e));
  };
}

function returnCheckoutFormErrors(contactFormErrors, ccFormErrors) {
  return {
    type: RETURN_CHECKOUT_FORM_ERRORS,
    contactFormErrors,
    ccFormErrors,
  };
}

export type PaymentFormValidator<T extends INewCreditCard = INewCreditCard> = (
  formValues: T
) => IErrors | null;

export interface PaymentSubmitHandlerResponse<T = Record<string, unknown>> {
  additionalData: T;
}

export type PaymentSubmitHandler<
  T extends INewCreditCard = INewCreditCard,
  TResponse = Record<string, unknown>,
> = (newCreditCard: T) => Promise<PaymentSubmitHandlerResponse<TResponse>>;

function submitOrder({
  paymentConfiguration,
  storeInfo,
  cart,
  checkout,
  consumerInfo,
  orderingOptions,
  sessionId,
}: {
  paymentConfiguration?: PaymentConfiguration | null;
  storeInfo: StoreInfo;
  cart: CartState;
  checkout: CheckoutState;
  consumerInfo: ConsumerState;
  orderingOptions: IOrderingOptionsState;
  sessionId: string;
}) {
  return async dispatch => {
    if (orderTimeExpired(orderingOptions, storeInfo)) {
      dispatch(setOrderTimeError());
      throw new Error("Order Time Validation Errors");
    }

    const contactFormErrors = validateContactInfo(
      consumerInfo.contactInfo,
      contactInfoValidators
    );
    const paymentFormErrors =
      paymentConfiguration?.paymentFormValidator?.(
        checkout.payment.newCreditCard
      ) ?? null;

    if (contactFormErrors || paymentFormErrors) {
      dispatch(returnCheckoutFormErrors(contactFormErrors, paymentFormErrors));
      throw new Error("Contact Form Validation Errors");
    }

    const paymentSubmitHandlerResponse =
      (await paymentConfiguration?.paymentSubmitHandler?.(
        checkout.payment.newCreditCard
      )) ?? null;

    const orderInfo: IOrderPayload = {
      ...orderTransform({
        storeInfo,
        checkout,
        consumerInfo,
        cart,
        orderingOptions,
        sessionId,
      }),
      ...paymentSubmitHandlerResponse?.additionalData,
    };

    dispatch(clearCheckoutFormErrors());

    return postOrder(orderInfo)
      .then(orderResponse => {
        const checkoutStatus = {
          ...checkout.status,
          confirmationCode: orderInfo.order.confirmationCode,
          ...(orderingOptions.selectedTimeFrame !== "Later"
            ? {
                orderTime: orderInfo.order.timePlaced,
                isOrderAhead: false,
              }
            : {
                orderTime: orderInfo.order.promisedDate,
                isOrderAhead: true,
              }),
        };

        dispatch(addOrderConfirmationDetails(checkoutStatus));

        saveOrderInfoToLocalStorage({
          contactInfo: consumerInfo.contactInfo,
          checkoutStatus,
          orderingOptions: orderingOptions,
          ccProcessor: checkout.payment?.newCreditCard.ccProcessor,
        });

        return orderResponse;
      })
      .catch(ex => {
        if (ex.response) {
          dispatch(parseOrderSubmissionError(ex.response));
        }
        return Promise.reject(ex);
      });
  };
}

function orderSuccess(isReturnToMenu = true) {
  return dispatch => {
    dispatch(updateOrderStatus("success"));
    dispatch(emptyCart());
    if (isReturnToMenu) {
      dispatch(returnToMenu());
    }
  };
}

// Returns an array of errors when validating the fields
function validateAllContactInfo(contact) {
  const contactFields = ["firstName", "lastName", "email", "phone"];
  // Return an error object with a null or string error.
  return map(field => {
    const error = validateBlurredField(
      field,
      contact,
      contactInfoValidators,
      VALIDATION_MESSAGES
    );
    return { field, error };
  }, contactFields);
}

function returnContactErrors(error, field) {
  return {
    type: VALIDATE_CONTACT_FIELD_ON_BLUR,
    error,
    field,
  };
}

function validateAndPutContactInfo(field, contact, saveOnBlur = false) {
  const fieldValidationErrors = validateAllContactInfo(contact);
  return dispatch => {
    let nanpValidation:
      ReturnType<typeof Validators.validatePhoneNANP> | undefined;
    let formattedPhone;
    // Check there are no errors
    if (all(pipe(prop("error"), isNil), fieldValidationErrors)) {
      nanpValidation = Validators.validatePhoneNANP(contact);
      if (nanpValidation.allowSave) {
        formattedPhone = contact.phone.replace(
          phoneRegex,
          nanpValidation.regex
        );
        dispatch(updateContactInfo({ phone: formattedPhone }));
      }

      if (isLoggedIn() && saveOnBlur) {
        if (equals(field, "phone")) {
          // These functions should not be returned we need to save the data AND
          // clear the error if there is one which happens in the action below
          if (nanpValidation.allowSave) postPhoneNumber(formattedPhone);
        } else {
          putConsumerInfo(
            contact.firstName,
            contact.lastName,
            contact.textAlerts
          );
        }
      }
    }
    forEach(error => {
      dispatch(returnContactErrors(error.error, error.field));
    }, fieldValidationErrors);
    if (
      !isNil(nanpValidation) &&
      !isNil(nanpValidation.error) &&
      contact.textAlerts // Don't show the NANP error if text updates is unchecked
    )
      dispatch(returnContactErrors(nanpValidation.error, nanpValidation.field));
  };
}

function validateAndSaveContactField(field, contact) {
  const fieldValidationErrors = validateAllContactInfo(contact);
  return dispatch => {
    let nanpValidation:
      ReturnType<typeof Validators.validatePhoneNANP> | undefined;
    let formattedPhone;
    if (
      (equals(field, "phone") || equals(field, "textAlerts")) &&
      isNil(find(propEq("phone", "field"), fieldValidationErrors)?.error)
    ) {
      nanpValidation = Validators.validatePhoneNANP(contact);
      if (nanpValidation.allowSave) {
        formattedPhone = contact.phone.replace(
          phoneRegex,
          nanpValidation.regex
        );
        dispatch(updateContactInfo({ phone: formattedPhone }));
      }
    }
    forEach(error => {
      if (
        equals(error.field, field) ||
        ((equals(field, "phone") || equals(field, "textAlerts")) &&
          equals(error.field, "phone"))
      ) {
        dispatch(returnContactErrors(error.error, error.field));
      }
    }, fieldValidationErrors);
    if (
      !isNil(nanpValidation) &&
      !isNil(nanpValidation.error) &&
      contact.textAlerts // Don't show the NANP error if text updates is unchecked
    )
      dispatch(returnContactErrors(nanpValidation.error, nanpValidation.field));
  };
}

export {
  fetchMenu,
  updateOrderingInfo,
  orderSuccess,
  submitOrder,
  validateAndPutContactInfo,
  validateAndSaveContactField,
};
